import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "../i18n/LangContext";

const industryIcons: Record<string, string> = {
  services: "support_agent",
  construction: "construction",
  consulting: "groups",
  technology: "memory",
  other: "more_horiz",
};

const sizeIcons: Record<string, string> = {
  solo: "person",
  small: "group",
  medium: "groups",
  large: "apartment",
  enterprise: "corporate_fare",
};

const API_URL = process.env.REACT_APP_API_URL || "";

const SignupPage: React.FC = () => {
  const { lang, t, setLang } = useLang();
  const s = t.signup;
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [industry, setIndustry] = useState("");
  const [businessSize, setBusinessSize] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("");
  const [currentTool, setCurrentTool] = useState("");
  const [topFeatures, setTopFeatures] = useState<string[]>([]);
  const [startTimeline, setStartTimeline] = useState("");

  const toggleFeature = (key: string) => {
    setTopFeatures((prev) =>
      prev.includes(key)
        ? prev.filter((k) => k !== key)
        : prev.length < 2
          ? [...prev, key]
          : prev
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg("");
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/waitlist`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          businessName,
          businessSize,
          industry,
          fullName,
          email,
          role: role || undefined,
          topFeatures,
          currentTool: currentTool || undefined,
          startTimeline: startTimeline || undefined,
        }),
      });
      if (res.status === 201) {
        setSubmitted(true);
      } else {
        const data = await res.json();
        setErrorMsg(data.error || "Something went wrong. Please try again.");
      }
    } catch {
      setErrorMsg("Unable to connect. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-bg-light font-display text-slate-900 min-h-screen flex flex-col">
      {/* HEADER */}
      <header className="w-full px-6 py-5 flex justify-between items-center bg-white border-b border-slate-200">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center text-white font-bold text-lg">W</div>
          <span className="text-xl font-bold tracking-tight text-primary">Worklly</span>
        </Link>
        <div className="flex items-center gap-4">
          <div className="relative group">
            <button className="flex items-center gap-1.5 px-3 py-2 rounded-lg hover:bg-primary/5 transition-colors text-sm font-medium text-slate-500">
              <span className="material-icons text-[18px]">language</span>
              <span>{t.nav.langLabel}</span>
              <span className="material-icons text-[14px]">expand_more</span>
            </button>
            <div className="absolute right-0 top-full pt-2 w-44 hidden group-hover:block z-50">
              <div className="bg-white rounded-xl shadow-xl border border-slate-100 py-2">
                <span
                  className={`flex items-center gap-3 px-4 py-2 hover:bg-primary/5 text-sm font-medium cursor-pointer ${lang === "fi" ? "text-primary" : "text-slate-500"}`}
                  onClick={() => setLang("fi")}
                >
                  <span className={`w-2 h-2 rounded-full ${lang === "fi" ? "bg-primary" : "border border-slate-300"}`} /> Suomi
                </span>
                <span
                  className={`flex items-center gap-3 px-4 py-2 hover:bg-primary/5 text-sm font-medium cursor-pointer ${lang === "en" ? "text-primary" : "text-slate-500"}`}
                  onClick={() => setLang("en")}
                >
                  <span className={`w-2 h-2 rounded-full ${lang === "en" ? "bg-primary" : "border border-slate-300"}`} /> English
                </span>
              </div>
            </div>
          </div>
          <div className="h-5 w-px bg-slate-200" />
          <Link className="text-sm font-semibold text-primary hover:underline" to="/">{s.backToHome}</Link>
        </div>
      </header>

      <main className="flex-1 flex items-stretch">
        <div className="max-w-[1400px] w-full mx-auto grid grid-cols-1 lg:grid-cols-2">

          {/* LEFT — VALUE PROPOSITION */}
          <div className="hidden lg:flex flex-col justify-center bg-gradient-to-br from-primary/[0.04] via-white to-primary/[0.08] px-12 xl:px-16 py-16 relative overflow-hidden">
            <div className="absolute top-12 right-12 w-64 h-64 bg-primary/5 rounded-full blur-3xl" />
            <div className="absolute bottom-16 left-8 w-48 h-48 bg-primary/5 rounded-full blur-3xl" />

            <div className="relative z-10">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-bold uppercase tracking-wider mb-8">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
                </span>
                {s.earlyAccessBadge}
              </span>

              <h1 className="text-4xl xl:text-5xl font-extrabold tracking-tight text-slate-900 leading-[1.1] mb-5">
                {s.heading}<span className="text-primary">{s.headingAccent}</span>
              </h1>

              <p className="text-lg text-slate-500 leading-relaxed mb-10 max-w-lg">
                {s.subtitle}
              </p>

              <ul className="space-y-4 mb-12">
                {s.features.map((feat, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <span className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <span className="material-icons text-primary text-[18px]">check</span>
                    </span>
                    <span className="text-slate-700 font-medium">{feat}</span>
                  </li>
                ))}
              </ul>

              <div className="bg-white/80 backdrop-blur-sm rounded-xl p-5 border border-primary/10 max-w-md">
                <div className="flex items-center gap-3 mb-2">
                  <span className="material-icons text-primary text-[22px]">verified</span>
                  <span className="text-sm font-bold text-slate-800">{s.earlyAccessNote}</span>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT — FORM */}
          <div className="flex items-center justify-center px-6 sm:px-10 lg:px-12 xl:px-16 py-12 bg-white">
            <div className="w-full max-w-md">
              {submitted ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                    <span className="material-icons text-green-500 text-[44px]">celebration</span>
                  </div>
                  <h1 className="text-3xl font-bold text-slate-900 mb-3">{s.successHeading}</h1>
                  <p className="text-slate-500 mb-8 leading-relaxed max-w-sm mx-auto">{s.successText}</p>
                  <Link to="/" className="inline-flex items-center gap-2 bg-primary text-white px-8 py-3 rounded-full font-bold hover:bg-primary/90 transition-all active:scale-95 shadow-lg shadow-primary/20">
                    <span className="material-icons text-[18px]">arrow_back</span> {s.successBack}
                  </Link>
                </div>
              ) : (
                <>
                  {/* Mobile heading */}
                  <div className="mb-6 lg:hidden text-center">
                    <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-bold uppercase tracking-wider mb-4">
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
                      </span>
                      {s.earlyAccessBadge}
                    </span>
                    <h1 className="text-2xl font-bold text-slate-900 mb-1">{s.heading}<span className="text-primary">{s.headingAccent}</span></h1>
                    <p className="text-slate-500 text-sm">{s.subtitle}</p>
                  </div>

                  <div className="mb-8">
                    <h2 className="text-2xl font-bold text-slate-900 mb-1">{s.formHeading}</h2>
                    <p className="text-slate-400 text-sm">{s.formSubtitle}</p>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-5">
                    {/* Business Name */}
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1.5" htmlFor="business_name">{s.businessNameLabel}</label>
                      <input
                        required id="business_name" name="business_name" type="text"
                        value={businessName} onChange={(e) => setBusinessName(e.target.value)}
                        placeholder={s.businessNamePlaceholder}
                        className="block w-full px-4 py-3 rounded-lg bg-slate-50 border border-slate-200 text-slate-900 focus:ring-2 focus:ring-primary focus:border-primary transition-all placeholder:text-slate-400 outline-none text-sm"
                      />
                    </div>

                    {/* Business Size */}
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">{s.businessSizeLabel}</label>
                      <div className="grid grid-cols-5 gap-2">
                        {Object.entries(s.businessSizes).map(([key, label]) => (
                          <button
                            key={key}
                            type="button"
                            onClick={() => setBusinessSize(key)}
                            className={`flex flex-col items-center gap-1 px-2 py-2.5 rounded-lg text-xs font-medium border transition-all ${
                              businessSize === key
                                ? "bg-primary text-white border-primary shadow-md shadow-primary/20"
                                : "bg-white text-slate-500 border-slate-200 hover:border-primary/40 hover:text-primary"
                            }`}
                          >
                            <span className="material-icons text-[20px]">{sizeIcons[key]}</span>
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Industry */}
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">{s.industryLabel}</label>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(s.industries).map(([key, label]) => (
                          <button
                            key={key}
                            type="button"
                            onClick={() => setIndustry(key)}
                            className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-medium border transition-all ${
                              industry === key
                                ? "bg-primary text-white border-primary shadow-md shadow-primary/20"
                                : "bg-white text-slate-500 border-slate-200 hover:border-primary/40 hover:text-primary"
                            }`}
                          >
                            <span className="material-icons text-[18px]">{industryIcons[key]}</span>
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Full Name */}
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1.5" htmlFor="full_name">{s.fullNameLabel}</label>
                      <input
                        required id="full_name" name="full_name" type="text"
                        value={fullName} onChange={(e) => setFullName(e.target.value)}
                        placeholder={s.fullNamePlaceholder}
                        className="block w-full px-4 py-3 rounded-lg bg-slate-50 border border-slate-200 text-slate-900 focus:ring-2 focus:ring-primary focus:border-primary transition-all placeholder:text-slate-400 outline-none text-sm"
                      />
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1.5" htmlFor="email">{s.emailLabel}</label>
                      <input
                        required id="email" name="email" type="email"
                        value={email} onChange={(e) => setEmail(e.target.value)}
                        placeholder={s.emailPlaceholder}
                        className="block w-full px-4 py-3 rounded-lg bg-slate-50 border border-slate-200 text-slate-900 focus:ring-2 focus:ring-primary focus:border-primary transition-all placeholder:text-slate-400 outline-none text-sm"
                      />
                    </div>

                    {/* Role */}
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1.5" htmlFor="role">{s.roleLabel}</label>
                      <input
                        id="role" name="role" type="text"
                        value={role} onChange={(e) => setRole(e.target.value)}
                        placeholder={s.rolePlaceholder}
                        className="block w-full px-4 py-3 rounded-lg bg-slate-50 border border-slate-200 text-slate-900 focus:ring-2 focus:ring-primary focus:border-primary transition-all placeholder:text-slate-400 outline-none text-sm"
                      />
                    </div>

                    {/* Top 2 Features */}
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1">
                        {s.topFeaturesLabel}
                      </label>
                      <p className="text-xs text-slate-400 mb-2.5">
                        {s.topFeaturesHint} — <span className="font-semibold text-primary">{topFeatures.length}/2</span>
                      </p>
                      <div className="grid grid-cols-2 gap-2">
                        {s.featureOptions.map((f) => {
                          const selected = topFeatures.includes(f.key);
                          const disabled = !selected && topFeatures.length >= 2;
                          return (
                            <button
                              key={f.key}
                              type="button"
                              onClick={() => toggleFeature(f.key)}
                              disabled={disabled}
                              className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium border transition-all text-left ${
                                selected
                                  ? "bg-primary text-white border-primary shadow-md shadow-primary/20"
                                  : disabled
                                    ? "bg-slate-50 text-slate-300 border-slate-100 cursor-not-allowed"
                                    : "bg-white text-slate-600 border-slate-200 hover:border-primary/40 hover:text-primary"
                              }`}
                            >
                              <span className="material-icons text-[18px]">{f.icon}</span>
                              {f.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Current Process/Tool */}
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1.5" htmlFor="current_tool">{s.currentToolLabel}</label>
                      <input
                        id="current_tool" name="current_tool" type="text"
                        value={currentTool} onChange={(e) => setCurrentTool(e.target.value)}
                        placeholder={s.currentToolPlaceholder}
                        className="block w-full px-4 py-3 rounded-lg bg-slate-50 border border-slate-200 text-slate-900 focus:ring-2 focus:ring-primary focus:border-primary transition-all placeholder:text-slate-400 outline-none text-sm"
                      />
                    </div>

                    {/* When would you like to start? */}
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">{s.startTimelineLabel}</label>
                      <div className="grid grid-cols-2 gap-2">
                        {Object.entries(s.startTimelines).map(([key, label]) => (
                          <button
                            key={key}
                            type="button"
                            onClick={() => setStartTimeline(key)}
                            className={`px-3 py-2.5 rounded-lg text-sm font-medium border transition-all text-left ${
                              startTimeline === key
                                ? "bg-primary text-white border-primary shadow-md shadow-primary/20"
                                : "bg-white text-slate-500 border-slate-200 hover:border-primary/40 hover:text-primary"
                            }`}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {errorMsg && (
                      <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
                        <span className="material-icons text-[18px]">error_outline</span>
                        {errorMsg}
                      </div>
                    )}

                    {/* Submit */}
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-primary hover:bg-primary/90 text-white font-bold py-3.5 px-4 rounded-lg shadow-lg shadow-primary/20 flex items-center justify-center gap-2 transition-all active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <>
                          <span className="material-icons animate-spin text-[20px]">autorenew</span>
                          {lang === "fi" ? "Lähetetään..." : "Submitting..."}
                        </>
                      ) : (
                        <>
                          {s.submitBtn}
                          <span className="material-symbols-outlined text-[20px]">arrow_forward</span>
                        </>
                      )}
                    </button>
                  </form>

                  <div className="mt-6 text-center">
                    <p className="text-xs text-slate-400 leading-relaxed">
                      {s.legal}{" "}
                      <a className="text-primary hover:underline underline-offset-4" href="#">{s.terms}</a>
                      {s.and}
                      <a className="text-primary hover:underline underline-offset-4" href="#">{s.privacy}</a>.
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </main>

      <footer className="w-full py-6 px-6 text-center text-xs text-slate-400 border-t border-slate-100">
        &copy; {s.footerCopyright}
        <span className="mx-2 text-slate-200">|</span>
        {s.footerTagline}
      </footer>
    </div>
  );
};

export default SignupPage;
